import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Plus, Trash2, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';
import { CATEGORIES, StarRating, OverallStars, overallScore, ScorecardFormDialog } from './ScorecardFormDialog';

const DriverScorecard = ({ vehicleId }) => {
  const [scorecards, setScorecards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchScorecards = useCallback(async () => {
    try {
      const data = await api.getScorecards(vehicleId);
      setScorecards(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { fetchScorecards(); }, [fetchScorecards]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this scorecard?')) return;
    try {
      await api.deleteScorecard(id);
      toast.success('Scorecard deleted');
      fetchScorecards();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Card className="border-slate-200" data-testid="driver-scorecard-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-heading flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-blue-600" />
            Driver Performance Scorecard
          </CardTitle>
          <Button size="sm" onClick={() => setDialogOpen(true)} className="bg-gray-700 hover:bg-gray-600 text-white" data-testid="add-scorecard-btn">
            <Plus className="w-4 h-4 mr-1" /> New Scorecard
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2].map(i => <div key={i} className="h-16 bg-slate-100 rounded" />)}
          </div>
        ) : scorecards.length === 0 ? (
          <p className="text-center text-slate-400 py-6 text-sm">No scorecards yet. Add a weekly driver performance review.</p>
        ) : (
          <div className="space-y-3">
            {scorecards.map((sc) => (
              <div key={sc.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50/50 transition-colors" data-testid={`scorecard-${sc.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-medium text-slate-900">{sc.driver_name}</p>
                    <p className="text-xs text-slate-500">Week of {sc.week_start}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <OverallStars value={sc.overall || overallScore(sc)} />
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(sc.id)} data-testid={`delete-scorecard-${sc.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {CATEGORIES.map(({ key, label }) => (
                    <div key={key} className="text-center">
                      <p className="text-[10px] text-slate-500 truncate">{label}</p>
                      <StarRating value={sc[key]} readonly />
                    </div>
                  ))}
                </div>
                {sc.notes && <p className="text-xs text-slate-500 mt-2 italic">{sc.notes}</p>}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <ScorecardFormDialog open={dialogOpen} onOpenChange={setDialogOpen} vehicleId={vehicleId} onSaved={fetchScorecards} />
    </Card>
  );
};

export default DriverScorecard;
